import { CopilotKit } from "@copilotkit/react-core"
import { useContext, type ReactNode } from "react"
import { ThreadContext } from "@/providers/ThreadProvider"
import { authHeaders } from "@/services/api.client"
import { casesService } from "@/features/cases/services/cases.service"
import { env } from "@/lib/env"
import { routeRunErrorEvent } from "@/features/oio/errors"
import { serviceForThread } from "@/services/services.registry"

interface CopilotKitProviderProps {
  children: ReactNode
}

export function CopilotKitProvider({ children }: CopilotKitProviderProps) {
  const thread = useContext(ThreadContext)

  // The agent is chosen by the ACTIVE CASE's order type, not by a global
  // setting: `appendMessage` runs whichever agent this prop names, so an appeal
  // run on "oio" would start the OIO graph on the appeal's thread id (the
  // checkpoint chain and Store key would collide, design D1). Absent or legacy
  // service ids resolve to OIO, so every existing case keeps agent="oio".
  // The value is stable for the provider's lifetime because AppShell keys the
  // whole subtree on the thread id; a case switch remounts it.
  const service = serviceForThread(thread?.activeThread)
  const { done: doneStatuses, failed: failedStatuses } = service.terminalStatuses

  // VITE_COPILOTKIT_SERVICE_URL is set to /api/v1/copilotkit at build time,
  // pointing to the Node.js CopilotKit runtime served via Nginx in the same container.
  const runtimeUrl = env.VITE_COPILOTKIT_SERVICE_URL

  const headers: Record<string, string> = { ...authHeaders() }
  if (thread?.activeThread?.id) {
    headers["X-Copilotkit-Thread-Id"] = thread.activeThread.id
  }

  const handleError = async (event: unknown) => {
    const message = extractCopilotErrorMessage(event, service.badge)
    const code = extractCopilotErrorCode(event)
    const source = extractCopilotErrorSource(event)

    console.error("[copilotkit] runtime error", { code, source, event })

    // Auth errors are session issues, not draft failures — suppress entirely.
    if (isAuthError(message)) {
      return
    }

    // The OIO run is now a DURABLE background task on the server — it keeps going
    // after the SSE stream drops. So a client-side stream error (a page refresh,
    // the laptop sleeping/waking, a flaky network) is NO LONGER a draft failure:
    // the run continues server-side and the reconnect poller adopts the result.
    //
    // The backend is therefore the SOLE authority on the end state — its compile
    // node writes draft_completed, and a genuine pipeline error writes
    // draft_failed_terminal. Re-fetch and let that status decide; never let a mere
    // transport teardown flip an in-flight (or finished) draft to "interrupted".
    // The status vocabulary is the order type's own (services.registry
    // terminalStatuses): OIO done = draft_completed/draft_filed, failed =
    // draft_failed_terminal; an appeal speaks draft_ready/.../failed.
    if (thread?.activeThread?.id) {
      try {
        const fresh = await casesService.get(thread.activeThread.id)
        const status = String((fresh?.metadata as Record<string, unknown>)?.draft_status ?? "")
        // Finished (or filed) — adopt it; the chat shows "ready", never interrupted.
        if (doneStatuses.includes(status)) {
          thread.setActiveThread?.(fresh)
          return
        }
        // Still in flight server-side — this onError is a transport teardown, not
        // a failure. Surface nothing and clobber nothing; the reconnect poller
        // will pick up the real result when the background run finishes.
        if (!failedStatuses.includes(status)) {
          return
        }
        // Backend genuinely failed — adopt its terminal status, then surface below.
        thread.setActiveThread?.(fresh)
        await thread.refreshThreads?.()
        // ...UNLESS THE WORKSPACE OWNS THE STATUS (services.registry
        // `statusOwner: "workspace"`, the appeal). That workspace already says
        // what failed, in place, with the one action that recovers it ("The
        // last revise failed … Draft again"). Re-opening such a case made
        // CopilotKit replay the OLD run's error event, and this handler raised
        // it again as a full-width "Something went wrong" over a page that was
        // already explaining it — two alarms for one failure, the vaguer one
        // on top. OIO (`statusOwner: "shell"`) has no such surface and keeps
        // the banner exactly as before.
        if (service.statusOwner === "workspace") {
          return
        }
      } catch {
        // Re-fetch failed — we can't confirm the state. Be conservative: do NOT
        // clobber a possibly-running draft to failed. Surface the error only.
      }
    }

    // Route to the error store — severity/category from backend metadata when available.
    routeRunErrorEvent(
      {
        type: "RUN_ERROR",
        message,
        code,
        metadata: extractCopilotErrorMetadata(event),
      },
      thread?.activeThread?.id ?? "",
    )
  }

  // Fix G3: pass threadId so CopilotKit routes to the correct LangGraph thread.
  // CONFIRMED (2026-06-19): in the installed @copilotkit/react-core (1.59.5),
  // <CopilotKit>'s own outer wrapper (CopilotKit -> CopilotKitProvider ->
  // CopilotKitInternal) DOES consume `agent`/`threadId`: CopilotKitInternal
  // reads `useThreads()`, fed by `<ThreadsProvider threadId={props.threadId}>`
  // (ThreadsProvider: `threadId = explicitThreadId ?? internalThreadId` —
  // the explicit value always wins), and sets its own top-level
  // CopilotChatConfigurationProvider's `agentId: props.agent ?? "default"`.
  // An earlier attempt here wrongly concluded these props were dropped
  // no-ops (it only read the inner CopilotKitProvider's destructured prop
  // list, not this outer wrapper) and replaced them with an explicit
  // CopilotChatConfigurationProvider missing `agentId` — that crashed
  // production with "Agent 'default' not found". Reverted to the original,
  // working mechanism. See docs/rewrite-requirements.md §9 for the
  // still-open question of why /stream and /rewrite/stream got mismatched
  // thread_ids in the first place — NOT this prop-forwarding path.
  // Fix G6: the parent (AppShell's ErrorBoundary) is keyed on activeThread.id,
  //          which forces a full remount whenever the user switches cases.
  return (
    <CopilotKit
      runtimeUrl={runtimeUrl}
      agent={service.agentName}
      headers={headers}
      threadId={thread?.activeThread?.id}
      onError={handleError}
      showDevConsole={false}
    >
      {children}
    </CopilotKit>
  )
}

function extractCopilotErrorMessage(event: unknown, badge: string): string {
  const rawMessage =
    getStringField(event, ["error", "message"]) ??
    getStringField(event, ["context", "technical", "message"]) ??
    getStringField(event, ["context", "request", "operation"])

  if (!rawMessage) {
    return "Draft generation was interrupted. Please retry."
  }
  if (rawMessage.toLowerCase() === "terminated") {
    return `Draft generation was interrupted before ${badge} could finish. Please retry.`
  }
  return rawMessage
}

function extractCopilotErrorCode(event: unknown): string {
  return getStringField(event, ["error", "code"]) ?? ""
}

function extractCopilotErrorSource(event: unknown): string {
  return getStringField(event, ["context", "source"]) ?? ""
}

function isAuthError(message: string): boolean {
  const m = message.toLowerCase()
  return (
    m.includes("unauthorized") ||
    m.includes("not authenticated") ||
    m.includes("invalid access token") ||
    m.includes("signature has expired")
  )
}

function extractCopilotErrorMetadata(event: unknown): {
  severity?: string
  category?: string
  retryable?: boolean
} {
  const meta = (event as Record<string, unknown>)?.metadata ?? {}
  if (typeof meta !== "object" || meta === null) return {}
  return {
    severity: typeof (meta as Record<string, unknown>).severity === "string"
      ? (meta as Record<string, unknown>).severity as string
      : undefined,
    category: typeof (meta as Record<string, unknown>).category === "string"
      ? (meta as Record<string, unknown>).category as string
      : undefined,
    retryable: typeof (meta as Record<string, unknown>).retryable === "boolean"
      ? (meta as Record<string, unknown>).retryable as boolean
      : undefined,
  }
}

function getStringField(value: unknown, path: string[]): string | null {
  let current: unknown = value
  for (const key of path) {
    if (!current || typeof current !== "object" || !(key in current)) {
      return null
    }
    current = (current as Record<string, unknown>)[key]
  }
  return typeof current === "string" ? current : null
}
